/* Leaft Blade V2 — small global behaviours.
   1) Arrival dates: [data-lb2-arrival] elements carry the dispatch calendar
      (ship days, cutoff, time zone, transit) from theme settings; the next
      arrival date is computed and rendered client-side. The cart drawer
      re-renders its DOM on every cart change, so a MutationObserver re-fills
      after updates. */

(() => {
  const ISO_BY_NAME = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

  // Next dispatch: today if it is a ship day and the cutoff has not passed,
  // otherwise the next ship day. Arrival = dispatch + courier transit.
  function arrivalDate(el) {
    const shipDays = (el.dataset.shipDays || '').split(',').map(Number).filter(Boolean);
    if (!shipDays.length) return null;
    const tz = el.dataset.tz || 'Pacific/Auckland';
    const cutoff = (el.dataset.cutoff || '12:00').split(':').map(Number);
    const transit = parseInt(el.dataset.transit, 10) || 1;
    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
      }).formatToParts(new Date());
    } catch (e) {
      return null;
    }
    const get = (t) => (parts.find((x) => x.type === t) || {}).value;
    const todayIso = ISO_BY_NAME[get('weekday')];
    if (!todayIso) return null;
    const beforeCutoff = Number(get('hour')) * 60 + Number(get('minute')) < cutoff[0] * 60 + (cutoff[1] || 0);
    let dispatch = null;
    for (let off = 0; off <= 8; off += 1) {
      const iso = ((todayIso - 1 + off) % 7) + 1;
      if (shipDays.includes(iso) && (off > 0 || beforeCutoff)) {
        dispatch = off;
        break;
      }
    }
    if (dispatch === null) return null;
    // A courier leg can span a range (US: 1 to 2 days); state both ends
    // rather than promising the slower one as if it were certain.
    const transitMax = parseInt(el.dataset.transitMax, 10) || transit;
    const fmt = (days) => new Intl.DateTimeFormat('en-NZ', {
      timeZone: tz, weekday: 'long', day: 'numeric', month: 'long',
    }).format(new Date(Date.now() + (dispatch + days) * 86400000));
    const first = fmt(transit);
    if (transitMax > transit) {
      const last = fmt(transitMax);
      if (last !== first) return `${first} or ${last}`;
    }
    return first;
  }

  function fillArrivals(root) {
    (root || document).querySelectorAll('[data-lb2-arrival]').forEach((el) => {
      const target = el.querySelector('[data-lb2-arrival-date]');
      if (!target || target.textContent) return;
      const date = arrivalDate(el);
      if (!date) return;
      target.textContent = date;
      el.hidden = false;
      const fallback = el.parentElement && el.parentElement.querySelector('[data-lb2-arrival-fallback]');
      if (fallback) fallback.hidden = true;
    });
  }

  function init() {
    fillArrivals(document);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => fillArrivals(drawer)).observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Leaft Blade V2 — instant add-to-cart feedback.
   The drawer opening is the real confirmation; this only covers the gap.
   Pressing an add button overlays "Adding" at once, flips to "Added" the
   moment the cart responds, and restores quickly - the drawer takes over.
   A hard timeout guarantees the button can never stay stuck. */
(() => {
  const timers = new WeakMap();

  function overlay(btn) {
    let el = btn.querySelector('.lb2-btn__state');
    if (!el) {
      el = document.createElement('span');
      el.className = 'lb2-btn__state';
      btn.appendChild(el);
    }
    return el;
  }

  function restore(btn) {
    const t = timers.get(btn);
    if (t) {
      clearTimeout(t.safety);
      clearTimeout(t.hold);
      clearTimeout(t.fallback);
      timers.delete(btn);
    }
    const el = btn.querySelector('.lb2-btn__state');
    if (el) el.remove();
    btn.removeAttribute('aria-busy');
  }

  function activeButtons() {
    return document.querySelectorAll('button.lb2-btn[aria-busy]');
  }

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.closest || !form.closest('product-form-component')) return;
    const btn = form.querySelector('button[type="submit"].lb2-btn');
    if (!btn || timers.has(btn)) return;
    btn.setAttribute('aria-busy', 'true');
    overlay(btn).textContent = 'Adding\u2026';
    timers.set(btn, { safety: setTimeout(() => restore(btn), 4000) });
  });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const comp = event.target && event.target.closest ? event.target.closest('product-form-component') : null;
    const btn = comp && comp.querySelector('button[type="submit"].lb2-btn');
    if (!btn || !timers.has(btn)) return;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      const t = timers.get(btn);
      if (!t) return;
      overlay(btn).textContent = 'Added \u2713';
      t.hold = setTimeout(() => restore(btn), 600);
    };
    // The deferred can be swallowed by downstream render work; the event itself
    // means the request is in flight, and failures raise shopify:cart:error.
    // Complete from whichever signal lands first.
    const t = timers.get(btn);
    if (t) t.fallback = setTimeout(done, 2000);
    const promise = event.promise || (event.detail && event.detail.promise);
    if (promise && typeof promise.then === 'function') {
      promise.then(done).catch(() => {
        settled = true;
        restore(btn);
      });
    }
  });

  document.addEventListener('shopify:cart:error', () => {
    activeButtons().forEach(restore);
  });
})();
